/**
 * Offline scan queue review (Req 13.1, 13.2, 28.4): scans waiting to be sent, claimed
 * scans (kept 7 days) and rejected scans with the server's reason. Reviewed items can be
 * discarded (with a confirm step); pending ones cannot. "Send now" / "Retry" flushes
 * immediately when online.
 */
import { useState } from 'react';
import { formatDateTime } from '@macksons/i18n';
import { format } from '@macksons/shortcode';
import { shortId } from '../auth/operator-scope';
import { useScanStatusText } from '../components/ClaimPanel';
import { CheckIcon, ClockIcon, CrossIcon } from '../components/icons';
import { useOnline } from '../components/ui';
import { useLocale, useScanQueue, useT } from '../hooks';
import { isPending, type QueuedScan } from '../offline/scan-queue';
import { useServices } from '../services';

function StatusIcon({ status }: { status: QueuedScan['status'] }) {
  if (status === 'done') return <CheckIcon className="list__status-icon" />;
  if (status === 'rejected') return <CrossIcon className="list__status-icon" />;
  return <ClockIcon className="list__status-icon" />;
}

/** Discard control with an inline confirmation step (reviewed items only). */
function DiscardControl({ item }: { item: QueuedScan }) {
  const t = useT();
  const { scanQueue } = useServices();
  const [confirming, setConfirming] = useState(false);
  const code = format(item.shortCode);
  if (confirming) {
    return (
      <div className="confirm" role="group" aria-label={t('operator.queue.discardLabel', { code })}>
        <p className="confirm__text">{t('operator.queue.discardConfirm')}</p>
        <button
          type="button"
          className="button button--danger"
          onClick={() => void scanQueue?.dismiss(item.idempotencyKey)}
          data-testid="queue-discard-confirm"
        >
          {t('operator.queue.discardYes')}
        </button>
        <button type="button" className="button" onClick={() => setConfirming(false)}>
          {t('operator.queue.discardNo')}
        </button>
      </div>
    );
  }
  return (
    <button type="button" className="button" aria-label={t('operator.queue.dismissLabel', { code })} onClick={() => setConfirming(true)}>
      {t('operator.queue.dismiss')}
    </button>
  );
}

function ItemList({ items, title, testId }: { items: readonly QueuedScan[]; title: string; testId: string }) {
  const t = useT();
  const locale = useLocale();
  const online = useOnline();
  const statusText = useScanStatusText();
  if (items.length === 0) return null;
  return (
    <section className="panel" aria-label={title}>
      <h2>{title}</h2>
      <ul className="list" data-testid={testId}>
        {items.map((item) => (
          <li key={item.idempotencyKey} className={`list__item list__item--${item.status}`}>
            <div className="list__main">
              <StatusIcon status={item.status} />
              <div className="list__text">
                <span className="code">{format(item.shortCode)}</span>
                <span>{statusText(item, online)}</span>
                <span className="hint">
                  {t('operator.queue.meta', { member: shortId(item.memberId), time: formatDateTime(item.scannedAt, locale) })}
                </span>
              </div>
            </div>
            {!isPending(item) && (
              <div className="list__actions">
                <DiscardControl item={item} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function QueuePage() {
  const t = useT();
  const online = useOnline();
  const { scanQueue } = useServices();
  const { items, pendingCount, flushing, lastFlush, loaded } = useScanQueue();
  const pending = items.filter(isPending);
  const rejected = items.filter((i) => i.status === 'rejected');
  const done = items.filter((i) => i.status === 'done');
  const canFlush = Boolean(scanQueue) && online && !flushing && pendingCount > 0;
  return (
    <>
      <h1>{t('operator.queue.title')}</h1>
      <p>{t('operator.queue.intro')}</p>
      <div className="actions">
        <button type="button" className="button button--primary" onClick={() => void scanQueue?.flush()} disabled={!canFlush}>
          {flushing ? t('operator.queue.sending') : t('operator.queue.retry')}
        </button>
      </div>
      {!canFlush && pendingCount > 0 && !online && (
        <p className="hint" data-testid="queue-disabled-hint">
          {t('operator.offline.needsConnection')}
        </p>
      )}
      <p role="status" aria-live="polite" data-testid="queue-summary">
        {lastFlush ? t('operator.queue.lastFlush', { done: lastFlush.done, rejected: lastFlush.rejected, pending: lastFlush.pending }) : ''}
      </p>
      {loaded && items.length === 0 && <p>{t('operator.queue.empty')}</p>}
      <ItemList items={pending} title={t('operator.queue.pending', { count: pending.length })} testId="queue-pending" />
      <ItemList items={rejected} title={t('operator.queue.rejected', { count: rejected.length })} testId="queue-rejected" />
      <ItemList items={done} title={t('operator.queue.done', { count: done.length })} testId="queue-done" />
    </>
  );
}
